// external packages
import { useEffect, useState } from 'react';
import {
	Box,
	IconButton,
	List,
	ListItem,
	Divider,
	Typography,
} from '@mui/material';

// components
import MuiCheckbox from '../../../mui/MuiCheckbox';
import { InvoiceViewPaymentInstallmentsHeader } from './InvoiceViewPaymentInstallmentsHeader';
import styles from './InvoiceViewPaymentInstallments.module.scss';
import { Icon } from '../../../blocks/icons';
import { Dropdown } from '../../Dropdown';
import { SubMenu } from './SubMenu';
import {
	getFormattedCurrency,
	getFormattedDateWithTimestamp,
} from '../../../lib/helpers';
import { preferredDayJsDateFormat } from '../../../lib/constants';

// logic
import {
	InstallmentStatus,
	InvoiceViewPaymentInstallmentsInstallmentsFragment,
	InvoiceViewPaymentInstallmentsInvoiceFragment,
} from '#types';
import { gql, DocumentNode } from '@apollo/client';
import {
	InvoiceViewPaymentInstallmentDetailsModal,
	InvoiceViewPaymentInstallmentDetailsModalProps,
} from './InvoiceViewPaymentInstallmentDetailsModal';
import {
	InvoiceViewPaymentInstallmentRefundModal,
	InvoiceViewPaymentInstallmentRefundModalProps,
} from './InvoiceViewPaymentInstallmentRefundModal';
import { InvoiceViewPaymentInstallmentStatus } from './InvoiceViewPaymentInstallmentStatus';
import {
	InvoiceViewPaymentInstallmentPaymentModal,
	InvoiceViewPaymentInstallmentPaymentModalProps,
} from './InvoiceViewPaymentInstallmentPaymentModal';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import rpcShared from '@rockpapercoin/rpc-shared';
import { newItemIdMarker } from '../../InvoiceEdit/usePaymentInstallments';

const invoiceViewPaymentInstallmentsLineItemsFragment = gql`
	fragment invoiceViewPaymentInstallmentsLineItems on LineItem {
		id
		name
		rate
		quantity
		taxPercentage
	}
`;

const invoiceViewPaymentInstallmentsInstallmentsFragment = gql`
	fragment invoiceViewPaymentInstallmentsInstallments on PaymentInstallment {
		id
		name
		convenienceFee
		amountPaid
		amountRefunded
		status
		amountDue
		gratuityPaid
		dueDate
		paymentDate
		...invoiceViewPaymentInstallmentDetailsModalPaymentInstallment
		...invoiceViewPaymentInstallmentStatus
		...invoiceViewPaymentInstallmentRefundModalPaymentInstallment
		...invoiceViewPaymentInstallmentPaymentModal
	}
	${ InvoiceViewPaymentInstallmentDetailsModal.fragments
		.invoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment }
	${ InvoiceViewPaymentInstallmentStatus.fragments
		.invoiceViewPaymentInstallmentStatusFragment }
	${ InvoiceViewPaymentInstallmentRefundModal.fragments
		.invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment }
	${ InvoiceViewPaymentInstallmentPaymentModal.fragments
		.invoiceViewPaymentInstallmentPaymentModalFragment }
`;

const invoiceViewPaymentInstallmentsInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentsInvoice on Invoice {
		id
		discountPercentage
		discountAmount
		lineItems {
			...invoiceViewPaymentInstallmentsLineItems
		}
		paymentInstallments {
			...invoiceViewPaymentInstallmentsSubMenuPaymentInstallments
			...invoiceViewPaymentInstallmentsInstallments
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			id
			country
			users {
				id
				user {
					id
				}
			}
		}
		...invoiceViewPaymentInstallmentHeaderInvoice
		...invoiceViewPaymentInstallmentsSubMenuInvoice
		...invoiceViewPaymentInstallmentPaymentModalInvoice
	}
	${ invoiceViewPaymentInstallmentsLineItemsFragment }
	${ invoiceViewPaymentInstallmentsInstallmentsFragment }
	${ SubMenu.fragments
		.invoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment }
	${ SubMenu.fragments.invoiceViewPaymentInstallmentsSubMenuInvoiceFragment }
	${ InvoiceViewPaymentInstallmentsHeader.fragments
		.invoiceViewPaymentInstallmentHeaderInvoiceFragment }
	${ InvoiceViewPaymentInstallmentPaymentModal.fragments
		.invoiceViewPaymentInstallmentPaymentModalInvoiceFragment }
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
`;

export type InvoiceViewPaymentInstallmentsProps = {
	invoice?: InvoiceViewPaymentInstallmentsInvoiceFragment;
	guestToken?: string;
	requestingUserBaseId?: string;
	shake: boolean;
	warning: boolean;
	selectedInstallments: {
		id: string;
		dueDate?: string | Date | null;
		amountDue?: number | null;
	}[];
	setSelectedInstallments: React.Dispatch<
		React.SetStateAction<
			{
				id: string;
				dueDate?: string | Date | null;
				amountDue?: number | null;
			}[]
		>
	>;
};

type InvoiceViewPaymentInstallmentsPropsWithFragment =
	React.FC<InvoiceViewPaymentInstallmentsProps> & {
		fragments: {
			invoiceViewPaymentInstallmentsInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentInstallments: InvoiceViewPaymentInstallmentsPropsWithFragment =
	( {
		invoice,
		guestToken,
		requestingUserBaseId,
		shake,
		warning,
		selectedInstallments,
		setSelectedInstallments,
	} ) => {
		const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>( null );
		const [ vendorSelectedInstallment, setVendorSelectedInstallment ] = useState<
			string | undefined
		>();
		const [ viewPaymentDetails, setViewPaymentDetails ] =
			useState<
				InvoiceViewPaymentInstallmentDetailsModalProps['paymentInstallment']
			>();
		const [ refund, setRefund ] =
			useState<
				InvoiceViewPaymentInstallmentRefundModalProps['paymentInstallment']
			>();
		const [ recordOrUpdate, setRecordOrUpdate ] =
			useState<
				InvoiceViewPaymentInstallmentPaymentModalProps['paymentInstallment']
			>();

		const { isMobile } = useDeviceDetection();

		useEffect( () => {
			if ( window.location.hash === '#payment-installments' ) {
				document.getElementById( 'payment-installments' )?.scrollIntoView();
			}
		}, [] );

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);
		const amountLabel = `Amount${ currencyLabel }`;

		const handleCustomerInstallmentClick = (
			checked: boolean,
			installment: InvoiceViewPaymentInstallmentsInstallmentsFragment
		) => {
			if ( checked ) {
				setSelectedInstallments( ( prevState ) => [ ...prevState, { id: installment.id, dueDate: installment.dueDate }, ] );
			} else {
				// remove the installmentId from the array of selected installments if it exists
				setSelectedInstallments( ( prevState ) =>
					prevState.filter(
						( selectedInstallment ) => selectedInstallment.id !== installment?.id
					)
				);
			}
		};

		const firstPayableInstallment = invoice?.paymentInstallments?.find(
			( installment ) =>
				installment.status === InstallmentStatus.Unpaid &&
				!installment.scheduledPaymentDate
		);

		const viewingUserIsVendorMember =
			invoice?.organization?.users?.some(
				( user ) => user.user?.id === requestingUserBaseId
			) || false;
		const { viewingUserIsCustomer, viewingUserIsAPPA } =
			invoice?.contact && requestingUserBaseId
				? rpcShared.contacts.getViewingUserDocumentRelationship( {
					contact: invoice.contact,
					viewingUserId: requestingUserBaseId,
				  } )
				: {
					viewingUserIsCustomer: false,
					viewingUserIsAPPA: false,
				  };

		const viewingUserIsCustomerOrAPPA =
			viewingUserIsCustomer || viewingUserIsAPPA;

		const getListItemStyle = () => {
			if ( !viewingUserIsVendorMember && !!firstPayableInstallment ) {
				return styles.listItemForCustomer;
			} else {
				return styles.listItemForVendor;
			}
		};

		return (
			<Box id='payment-installments' className={ styles.container }>
				<InvoiceViewPaymentInstallmentsHeader
					invoice={ invoice }
					warning={ warning }
					requestingUserBaseId={ requestingUserBaseId }
					amountLabel={ amountLabel }
				/>
				<List disablePadding className={ styles.installmentList }>
					{ invoice?.paymentInstallments?.map( ( installment, key ) => {
						if ( typeof installment.id !== 'string' ) {
							return null;
						}
						return (
							<Box key={ key }>
								<ListItem disableGutters className={ getListItemStyle() }>
									{ !viewingUserIsVendorMember && !!firstPayableInstallment ? (
										<MuiCheckbox
											slotProps={ {
												input: { 'data-cy': 'installment-selector' },
											} }
											id={ installment.id }
											className={
												shake && installment.id === firstPayableInstallment?.id
													? styles.shake
													: styles.selector
											}
											checked={ selectedInstallments?.some(
												( previouslySelectedInstallment ) =>
													installment.id === previouslySelectedInstallment.id
											) }
											disabled={ installment.status !== InstallmentStatus.Unpaid }
											onChange={ ( _, checked ) =>
												installment.id
													? handleCustomerInstallmentClick( checked, installment )
													: undefined
											}
											error={
												shake && installment.id === firstPayableInstallment?.id
											}
										/>
									) : null }
									<Box className={ styles.name } data-cy='installment-value-name'>
										<Typography>{ installment.name }</Typography>
									</Box>
									<Box className={ styles.amountDue }>
										{ isMobile ? (
											<Box data-cy='mobile-label-amount'>
												<Typography className={ styles.label }>
													{ amountLabel }
												</Typography>
											</Box>
										) : null }
										<Box data-cy='installment-value-amountDue'>
											<Typography className={ styles.value }>
												{ installment.amountDue
													? getFormattedCurrency( installment.amountDue )
													: '-' }
											</Typography>
										</Box>
									</Box>
									<Box className={ styles.dueDate }>
										{ isMobile ? (
											<Box data-cy='mobile-label-dueDate'>
												<Typography className={ styles.label }>
													Due date
												</Typography>
											</Box>
										) : null }
										<Box data-cy='installment-value-dueDate'>
											<Typography className={ styles.value }>
												{ installment.dueDate
													? getFormattedDateWithTimestamp(
														installment.dueDate,
														preferredDayJsDateFormat
													  )
													: '-' }
											</Typography>
										</Box>
									</Box>
									<Box className={ styles.paymentDate }>
										{ isMobile ? (
											<Box data-cy='mobile-label-paymentDate'>
												<Typography className={ styles.label }>
													Date paid
												</Typography>
											</Box>
										) : null }
										<Box data-cy='installment-value-paymentDate'>
											<Typography className={ styles.value }>
												{ installment.paymentDate
													? getFormattedDateWithTimestamp(
														installment.paymentDate,
														preferredDayJsDateFormat
													  )
													: '-' }
											</Typography>
										</Box>
									</Box>
									<Box className={ styles.status }>
										<InvoiceViewPaymentInstallmentStatus
											installment={ installment }
											isCustomerOrAPPA={ viewingUserIsCustomerOrAPPA }
										/>
									</Box>
									<Box className={ styles.dotMenu } data-cy='installment-dotMenu'>
										<IconButton
											className={
												vendorSelectedInstallment === installment.id
													? styles.active
													: ''
											}
											disabled={ installment.id.startsWith( newItemIdMarker ) }
											onClick={ ( event: React.MouseEvent<HTMLElement> ) => {
												setVendorSelectedInstallment( installment.id );
												setAnchorEl( event.currentTarget );
											} }
										>
											<Icon type='more' />
										</IconButton>
									</Box>
									<Dropdown
										onClose={ () => setVendorSelectedInstallment( undefined ) }
										isShown={ vendorSelectedInstallment === installment.id }
										anchorEl={ anchorEl }
										placement='bottom-end'
									>
										<SubMenu
											invoice={ invoice }
											requestingUserBaseId={ requestingUserBaseId }
											installment={ installment }
											handleClose={ () =>
												setVendorSelectedInstallment( undefined )
											}
											onViewDetails={ setViewPaymentDetails }
											onRefund={ setRefund }
											onRecordOrUpdate={ setRecordOrUpdate }
										/>
									</Dropdown>
								</ListItem>
								<Divider />
							</Box>
						);
					} ) }
				</List>
				<InvoiceViewPaymentInstallmentDetailsModal
					paymentInstallment={ viewPaymentDetails }
					onClose={ () => setViewPaymentDetails( undefined ) }
					requestingUserBaseId={ requestingUserBaseId }
					invoice={ invoice }
					guestToken={ guestToken }
				/>
				<InvoiceViewPaymentInstallmentRefundModal
					paymentInstallment={ refund }
					onClose={ () => setRefund( undefined ) }
					requestingUserBaseId={ requestingUserBaseId }
					invoice={ invoice }
				/>
				<InvoiceViewPaymentInstallmentPaymentModal
					invoice={ invoice }
					requestingUserBaseId={ requestingUserBaseId }
					paymentInstallment={ recordOrUpdate }
					onClose={ () => setRecordOrUpdate( undefined ) }
				/>
			</Box>
		);
	};

InvoiceViewPaymentInstallments.fragments = {
	invoiceViewPaymentInstallmentsInvoiceFragment,
};
